import { type PortalMember } from "@/hooks/useUsers";
import { Button } from "@simplicity/ui";
import { Plus, Users } from "lucide-react";
import { useState } from "react";

import { GroupDetail } from "./GroupDetail";
import { GroupRow } from "./GroupRow";
import { SectionHeader } from "./SectionHeader";
import { type GroupDashboard } from "./types";

export function UserGroupsSection({
  groups,
  dashboards,
  users,
  defaultGroupId,
  onSetDefaultGroup,
  onRenameGroup,
  onDeleteGroup,
  onCreateGroup,
  onError,
}: {
  groups: { id: string; name: string }[];
  dashboards: GroupDashboard[];
  users: PortalMember[];
  defaultGroupId: string | null;
  onSetDefaultGroup: (id: string | null) => void;
  onRenameGroup: (group: { id: string; name: string }) => void;
  onDeleteGroup: (id: string) => void;
  onCreateGroup: () => void;
  onError: (msg: string) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  return (
    <section className="mb-10">
      <SectionHeader
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        icon={<Users className="h-5 w-5" />}
        title="User Groups"
        actions={
          <Button size="sm" onClick={onCreateGroup}>
            <Plus className="h-4 w-4" /> New Group
          </Button>
        }
      />
      {!collapsed &&
        (groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No user groups yet. Create one to get started.
          </p>
        ) : (
          <div className="border border-border rounded divide-y divide-border">
            {groups.map((group) => (
              <GroupRow
                key={group.id}
                group={group}
                isDefault={defaultGroupId === group.id}
                isExpanded={expandedGroup === group.id}
                onToggle={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
                onRename={() => onRenameGroup(group)}
                onDelete={() => onDeleteGroup(group.id)}
              >
                <GroupDetail
                  groupId={group.id}
                  isDefault={defaultGroupId === group.id}
                  onSetDefault={onSetDefaultGroup}
                  users={users}
                  dashboards={dashboards}
                  onError={onError}
                />
              </GroupRow>
            ))}
          </div>
        ))}
    </section>
  );
}
